<template>
	<div class="monthly-chart">
		<line-chart class="chart-frame" :chart-data="chartData" :options="options"></line-chart>
	</div>
</template>

<script>
	import { DateTime } from 'luxon';
	import LineChart from '@/modules/charts/LineChart';

	export default {
		name: 'InvoiceByMonth',

		props: {
			invoices: { type: Array, required: false, default: () => [] },
		},

		components: { LineChart },

		computed: {
			chartColor() {
				return this.$vuetify.theme.currentTheme.primary || '#267AE1';
			},

			options() {
				return {
					showTooltips: true,
					responsive: true,
					maintainAspectRatio: false,
					legend: {
						display: false,
					},
					elements: {
						point: {
							radius: 0,
							hoverRadius: 5,
							hitRadius: 12,
							borderWidth: 2,
						},
						line: {
							tension: 0.34,
							borderWidth: 2,
						},
					},
					scales: {
						xAxes: [
							{
								display: true,
								gridLines: {
									display: false,
								},
								ticks: {
									autoSkip: true,
									maxTicksLimit: 8,
									maxRotation: 0,
									minRotation: 0,
									fontColor: '#867F77',
								},
							},
						],
						yAxes: [
							{
								display: true,
								position: 'left',
								gridLines: {
									color: 'rgba(134, 127, 119, 0.14)',
									drawBorder: false,
									zeroLineColor: 'rgba(134, 127, 119, 0.22)',
								},
								ticks: {
									beginAtZero: true,
									maxTicksLimit: 6,
									fontColor: '#867F77',
									callback: (value) =>
										this.$formatters.dollars(value, false, true, this.$store.state.defaultCurrency),
								},
							},
						],
					},
					tooltips: {
						displayColors: false,
						backgroundColor: 'rgba(16, 17, 15, 0.92)',
						titleFontStyle: '600',
						cornerRadius: 8,
						xPadding: 12,
						yPadding: 10,
						callbacks: {
							title: (tooltipItems) => tooltipItems[0].xLabel,
							label: (tooltipItem, data) => {
								const amount = this.$formatters.dollars(
									tooltipItem.value,
									true,
									true,
									this.$store.state.defaultCurrency
								);
								return `${data.datasets[tooltipItem.datasetIndex].label}: ${amount}`;
							},
						},
					},
				};
			},

			chartData() {
				return {
					labels: this.labels,
					datasets: [
						{
							label: this.$t('invoices.reports.monthly-invoiced'),
							backgroundColor: this.withAlpha(this.chartColor, 0.12),
							borderColor: this.chartColor,
							pointBackgroundColor: this.chartColor,
							pointBorderColor: this.$vuetify.theme.currentTheme.white,
							data: this.invoiced,
							fill: true,
						},
					],
				};
			},

			labels() {
				return this.invoiceByMonth.map((invoiceMonth) => invoiceMonth.label);
			},

			invoiced() {
				return this.invoiceByMonth.map((invoiceMonth) => invoiceMonth.total);
			},

			invoiceByMonth() {
				if (!this.invoices.length) {
					return [];
				}

				const invoices = [...this.invoices].sort(this.sortByDateSent);
				const earliest = DateTime.fromISO(invoices[0].dateSent).startOf('month');
				const latest = DateTime.fromISO(invoices[invoices.length - 1].dateSent).startOf('month');
				const result = [];
				let start = earliest;

				while (start <= latest) {
					result.push({
						key: start.toISODate(),
						date: start,
						label: start.toFormat('MMM yy'),
						total: 0,
					});
					start = start.plus({ months: 1 });
				}

				invoices.forEach((invoice) => {
					const key = DateTime.fromISO(invoice.dateSent).startOf('month').toISODate();
					const month = result.find((resultMonth) => resultMonth.key === key);
					if (month) {
						month.total += invoice.total;
					}
				});

				return result;
			},
		},

		methods: {
			sortByDateSent(invoiceA, invoiceB) {
				if (invoiceA.dateSent && invoiceB.dateSent) {
					return invoiceA.dateSent.localeCompare(invoiceB.dateSent);
				}
				if (invoiceA.dateSent) {
					return -1;
				}
				if (invoiceB.dateSent) {
					return 1;
				}
				return 0;
			},

			withAlpha(color, alpha) {
				const hex = color && color.match(/^#([\da-f]{6})$/i);
				if (!hex) {
					return color;
				}
				const value = parseInt(hex[1], 16);
				return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
			},
		},
	};
</script>

<style scoped lang="scss">
	.monthly-chart {
		height: 330px;
		padding: 18px 16px 14px;
	}

	.chart-frame {
		position: relative;
		height: 100%;
	}

	.monthly-chart ::v-deep canvas {
		width: 100% !important;
		height: 100% !important;
	}

	@media (max-width: 599px) {
		.monthly-chart {
			height: 280px;
			padding: 14px 10px 10px;
		}
	}
</style>
